import {
	Box,
	Heading,
	Text,
	VStack,
	Divider,
	Input,
	InputGroup,
	InputLeftElement,
	Icon,
	Link,
	HStack,
	IconButton,
	useDisclosure
} from "@chakra-ui/react";
import FullScreenSection from "./FullScreenSection";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { FaSearch, FaPhoneAlt, FaComments } from "react-icons/fa";
import LiveChat from "./LiveChat";
import Disclosurre from "./disclosurre";
import { resourceSections } from "./resourceFaqData";
import { renderFaqTextWithLinks } from "./faqTextLinks";

export default function Resources() {
	const location = useLocation();
	const [query, setQuery] = useState("");
	const [activeId, setActiveId] = useState("");
	const { isOpen, onOpen, onClose } = useDisclosure();

		useEffect(() => {
			if (!location.hash) return;
			const id = location.hash.replace("#", "");
			setActiveId(id);
		}, [location.hash]);

		const searchResults = useMemo(() => {
			if (!query.trim()) return [];
			const lowerQuery = query.toLowerCase();
			return resourceSections.filter((section) =>
                `${section.title} ${section.description} ${section.body} ${(section.links || []).map((item) => item.label || "").join(" ")}`
                    .toLowerCase()
                    .includes(lowerQuery)
			);
		}, [query]);

		const visibleSections = useMemo(() => {
			if (activeId) {
				return resourceSections.filter((section) => section.id === activeId);
			}
            if (!query.trim()) {
                return resourceSections;
            }
            return [];
        }, [activeId, query]);

		return (
        <VStack spacing={0} width="100%">
            <FullScreenSection
                    justifyContent="center"
                    alignItems="center"
                    isDarkBackground={false}
                    backgroundColor="gray.50"
                    py={100}
            >
                    <VStack spacing={10} width="100%" maxW="1100px" align="stretch">
                        <VStack spacing={3} textAlign="center">
                            <Heading fontSize={{ base: "2.2rem", md: "3rem" }} fontWeight="bold">
                                Resources
                            </Heading>
                            <Text color="gray.600" fontSize="1.1rem">
                                Find quick answers and guidance for common Swift Credit questions.
                            </Text>
                        </VStack>

                        <Box width="100%">
                            <InputGroup size="lg">
                                <InputLeftElement pointerEvents="none">
                                    <Icon as={FaSearch} color="gray.400" />
                                </InputLeftElement>
                                <Input
                                    placeholder="Search for your question"
                                    bg="white"
                                    value={query}
                                    onChange={(event) => setQuery(event.target.value)}
                                />
                            </InputGroup>

                            {query.trim() && searchResults.length > 0 && (
                                <Box mt={3} bg="white" borderRadius="lg" boxShadow="md" p={4} textAlign="left">
                                    <VStack spacing={3} align="stretch">
                                        {searchResults.map((section) => (
                                            <Link
                                                key={section.id}
                                                href="/resources"
                                                color="blue.600"
                                                fontWeight="semibold"
                                                onClick={(event) => {
                                                    event.preventDefault();
                                                    setActiveId(section.id);
                                                    setQuery("");
                                                }}
                                            >
                                                <Text>{section.title}</Text>
                                                <Text fontSize="sm" color="gray.600">
                                                    {section.description}
                                                </Text>
                                            </Link>
                                        ))}
                                    </VStack>
                                </Box>
                            )}

                        </Box>

                        <VStack spacing={10} align="stretch">
                            {visibleSections.map((section) => (
                                <Box key={section.id} id={section.id} bg="white" borderRadius="xl" boxShadow="md" p={{ base: 6, md: 8 }}>
                                    <Heading fontSize={{ base: "1.6rem", md: "2rem" }} mb={3}>
                                        {section.title}
                                    </Heading>
                                    <Text color="gray.600" lineHeight="1.8" whiteSpace="pre-line">
                                        {renderFaqTextWithLinks(section.body)}
                                    </Text>
                                    {Array.isArray(section.links) && section.links.length > 0 && (
                                        <VStack align="flex-start" spacing={2} mt={4}>
                                            {section.links.map((linkItem) => (
                                                <Link
                                                    key={`${section.id}-${linkItem.label}`}
                                                    href={linkItem.href || "#"}
                                                    color="blue.600"
                                                    fontWeight="semibold"
                                                    onClick={(event) => {
                                                        if (linkItem.type === "chat") {
                                                            event.preventDefault();
                                                            onOpen();
                                                        }
                                                    }}
                                                >
                                                    {linkItem.label}
                                                </Link>
                                            ))}
                                        </VStack>
                                    )}
                                    <Divider mt={6} />
                                </Box>
                            ))}
							{query.trim() && visibleSections.length === 0 && (
                                <Box bg="white" borderRadius="xl" boxShadow="md" p={{ base: 6, md: 8 }}>
                                    <Heading fontSize={{ base: "1.4rem", md: "1.6rem" }} mb={2}>
                                        No results found
                                    </Heading>
                                    <Text color="gray.600">Try searching for a different question.</Text>
                                </Box>
                            )}

                            <VStack align="center" spacing={5}>
                                <Heading color="blue.700" fontSize="1.5rem">Can’t find what you’re looking for?</Heading>
                                <VStack align="center" spacing={4}>
                                    <Heading fontSize="1.2rem">Get help 24/7</Heading>
                                    <HStack spacing={7}>
                                        <HStack spacing={3}>
                                            <Icon as={FaPhoneAlt} boxSize={5} color="gray.600" />
                                            <Text fontWeight="semibold">+17802422478</Text>
                                        </HStack>
                                        <VStack align="flex-start" spacing={1}>
                                            <Link href="#" color="gray.600" fontWeight="semibold" onClick={onOpen}>
                                                <HStack spacing={3}>
                                                    <Icon as={FaComments} boxSize={5} />
                                                    <Text whiteSpace="nowrap">Chat</Text>
                                                </HStack>  
                                            </Link>
                                        </VStack>
                                    </HStack>
                                </VStack>
                            </VStack>
                        </VStack>
                    </VStack>
				
								
                <IconButton
                    aria-label="Open live chat"
                    icon={<FaComments size={36} />}
                    position="fixed"
                    bottom={{ base: 5, md: 6 }}
                    right={{ base: 5, md: 6 }}
                    borderRadius="full"
                    bg="black"
                    color="white"
                    boxShadow="lg"
                    boxSize={{ base: "50px", md: "58px" }}
                    _hover={{ bg: "gray.800", transform: "scale(1.05)" }}
                    onClick={onOpen}
                    zIndex="tooltip"
                />
                <LiveChat isOpen={isOpen} onClose={onClose} />
            </FullScreenSection>  
		<Disclosurre />
			
        </VStack>
		);
	}
